import { useRef, useState } from "react";
import axios from "axios";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

/**
 * ImageUpload — drag-and-drop OR click image uploader for admin product photos.
 *
 * Props:
 *  - value: current image URL (string or "")
 *  - onChange(url): called with the absolute URL of the uploaded image
 *  - authHeaders: axios auth headers
 *  - testId: optional test id
 */
export const ImageUpload = ({ value, onChange, authHeaders, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Alleen afbeeldingen kunnen worden geüpload.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Bestand is te groot (max 8 MB).");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("kind", "product");
      const res = await axios.post(`${API}/admin/media/upload`, formData, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url || res.data.relative_url);
      toast.success("Afbeelding geüpload");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload mislukt");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div data-testid={testId}>
      <div
        className={`relative border-2 border-dashed transition-colors p-4 ${
          dragging
            ? "border-[var(--tha-red)] bg-[#1a0000]"
            : "border-[var(--tha-border)] bg-[#050505] hover:border-[var(--tha-muted)]"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={testId ? `${testId}-input` : undefined}
        />

        {value ? (
          <div className="flex items-start gap-4">
            <img
              src={value}
              alt="preview"
              className="w-24 h-24 object-cover border border-[var(--tha-border)]"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                HUIDIGE AFBEELDING
              </div>
              <div className="text-xs font-mono-x text-white truncate mt-1">{value}</div>
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                  className="border border-[var(--tha-border)] hover:border-white text-xs font-mono-x tracking-widest px-3 py-2 inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid={testId ? `${testId}-replace` : undefined}
                >
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                  {uploading ? "BEZIG…" : "VERVANG"}
                </button>
                <button
                  type="button"
                  onClick={() => onChange("")}
                  className="border border-[var(--tha-border)] text-[var(--tha-muted)] hover:border-red-700 hover:text-red-500 text-xs font-mono-x tracking-widest px-3 py-2 inline-flex items-center gap-2"
                  data-testid={testId ? `${testId}-clear` : undefined}
                >
                  <X size={12} /> WIS
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-full py-6 flex flex-col items-center justify-center gap-2 text-[var(--tha-muted)] hover:text-white disabled:opacity-60"
            data-testid={testId ? `${testId}-trigger` : undefined}
          >
            {uploading ? (
              <Loader2 size={28} className="animate-spin text-[var(--tha-red)]" />
            ) : (
              <ImageIcon size={28} />
            )}
            <div className="text-xs font-mono-x tracking-widest">
              {uploading ? "BEZIG MET UPLOADEN…" : "SLEEP HIER OF KLIK"}
            </div>
            <div className="text-[10px] text-[var(--tha-muted)] font-mono-x">
              JPG · PNG · WebP · GIF — max 8 MB
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * ReviewPhotoUpload — token-gated, multi-image (max 3) photo uploader used on the public review form.
 *
 * Props:
 *  - token: review token (string)
 *  - photos: current uploaded photos [{id, url, ...}]
 *  - onChange(photos[]): updates the list
 *  - disabled: bool (e.g. before review is submitted)
 */
export const ReviewPhotoUpload = ({ token, photos, onChange, disabled, helper }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const max = 3;
  const remaining = max - photos.length;

  const handleFiles = async (fileList) => {
    if (disabled) return;
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList).slice(0, remaining);
    if (files.length === 0) {
      toast.error(`Maximaal ${max} foto's per review.`);
      return;
    }
    setUploading(true);
    const next = [...photos];
    for (const f of files) {
      if (!f.type.startsWith("image/")) {
        toast.error(`"${f.name}" is geen afbeelding.`);
        continue;
      }
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`"${f.name}" is groter dan 8MB.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/review/${token}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(res.data);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : `Upload van "${f.name}" mislukt`);
      }
    }
    onChange(next);
    setUploading(false);
  };

  return (
    <div data-testid="review-photo-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="review-photo-input"
      />
      <div className="flex flex-wrap gap-3">
        {photos.map((p) => (
          <div
            key={p.id}
            className="relative w-24 h-24 border border-[var(--tha-border)] bg-[#050505] overflow-hidden"
          >
            <img src={p.url || p.relative_url} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
        {photos.length < max && (
          <button
            type="button"
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
            className={`w-24 h-24 flex flex-col items-center justify-center gap-1 border-2 border-dashed transition-colors ${
              disabled
                ? "border-[var(--tha-border)] text-[var(--tha-muted)] opacity-50 cursor-not-allowed"
                : "border-[var(--tha-border)] text-[var(--tha-muted)] hover:border-[var(--tha-red)] hover:text-[var(--tha-red)]"
            }`}
            data-testid="review-photo-add"
          >
            {uploading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Upload size={20} />
            )}
            <span className="text-[9px] font-mono-x tracking-widest">FOTO</span>
          </button>
        )}
      </div>
      <div className="mt-2 text-[10px] font-mono-x text-[var(--tha-muted)]">
        {helper || `Optioneel · ${photos.length}/${max} foto's · JPG/PNG/WebP · max 8 MB per stuk`}
      </div>
    </div>
  );
};
